import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Generate from './pages/Generate';
import Edit from './pages/Edit';
import CVBuilderV2 from './pages/CVBuilderV2';
import CVBuilderV3 from './pages/CVBuilderV3';
import AdminUsers from './pages/AdminUsers';
import AdminTemplateQA from './pages/AdminTemplateQA';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Superadmin Route Component
const SuperadminRoute = ({ children }) => {
  const { user, loading, isSuperadmin } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperadmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/generate"
        element={
          <ProtectedRoute>
            <Generate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit/:cvId"
        element={
          <ProtectedRoute>
            <Edit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cv-builder-v2"
        element={
          <ProtectedRoute>
            <CVBuilderV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cv-builder-v2/:cvId"
        element={
          <ProtectedRoute>
            <CVBuilderV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cv-builder-v3"
        element={
          <ProtectedRoute>
            <CVBuilderV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cv-builder-v3/:cvId"
        element={
          <ProtectedRoute>
            <CVBuilderV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <SuperadminRoute>
            <AdminUsers />
          </SuperadminRoute>
        }
      />
      <Route
        path="/admin/template-qa"
        element={
          <SuperadminRoute>
            <AdminTemplateQA />
          </SuperadminRoute>
        }
      />
      <Route
        path="/admin/template-qa/:builder"
        element={
          <SuperadminRoute>
            <AdminTemplateQA />
          </SuperadminRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
